import IconLink from '@apollo/icons/default/IconLink.svg';
import classnames from 'classnames';
import copy from 'copy-to-clipboard';
import { DocumentNode } from 'graphql';
import _ from 'lodash';
import * as monacoEditor from 'monaco-editor';
import React from 'react';
import { createPortal } from 'react-dom';
import { useHistory } from 'react-router';

import {
  getHashParamsFromLineNumber,
  getLinkableLineNumbers,
} from 'src/app/graph/documentationPage/schemaPage/sdlEditorHelpers';
import { useDerivedEditorValue } from 'src/app/graph/explorerPage/hooks/useDerivedEditorValue';
import { useInternalGraphLinking } from 'src/app/graph/hooks/useInternalGraphLinking';
import { Tooltip } from 'src/components/common/tooltip/Tooltip';

export const LineLinks = ({
  editor,
  typeDefs,
  selectedSchema,
}: {
  editor: monacoEditor.editor.IStandaloneCodeEditor;
  typeDefs: DocumentNode;
  selectedSchema: string | undefined;
}) => {
  const definitionTypeForNumber = React.useMemo(
    () => getLinkableLineNumbers(typeDefs),
    [typeDefs],
  );

  const [visibleRange, setVisibleRange] = React.useState<
    monacoEditor.Range | undefined
  >(editor.getVisibleRanges()[0]);

  const [mouseLineNumber, setMouseLineNumber] = React.useState<
    number | undefined
  >(undefined);

  React.useEffect(() => {
    const disposables = [
      editor.onDidScrollChange(() => {
        const visibleRanges = editor.getVisibleRanges();

        setVisibleRange(visibleRanges[0]);
      }),

      editor.onMouseMove(({ target }) => {
        setMouseLineNumber(target?.position?.lineNumber);
      }),
    ];

    return () => {
      disposables.forEach((disposable) => disposable?.dispose());
    };
  }, [editor]);

  return (
    <div>
      {Object.entries(definitionTypeForNumber).map(
        ([lineNumberString, definitionType]) => {
          const lineNumber = parseInt(lineNumberString, 10);
          if (
            visibleRange &&
            lineNumber >= visibleRange.startLineNumber &&
            lineNumber <= visibleRange.endLineNumber
          ) {
            return (
              <LineLink
                key={lineNumber}
                lineNumber={lineNumber}
                definitionType={definitionType}
                editor={editor}
                typeDefs={typeDefs}
                selectedSchema={selectedSchema}
                showLink={mouseLineNumber === lineNumber}
              />
            );
          }
          return null;
        },
      )}
    </div>
  );
};

const LineLink = ({
  editor,
  lineNumber,
  definitionType,
  typeDefs,
  selectedSchema,
  showLink,
}: {
  editor: monacoEditor.editor.IStandaloneCodeEditor;
  lineNumber: number;
  definitionType: string;
  typeDefs: DocumentNode;
  selectedSchema: string | undefined;
  showLink: boolean;
}) => {
  const marginDomNode = React.useMemo(() => document.createElement('div'), []);

  const currentViewZone = React.useRef<{
    zoneId: string;
    zone: monacoEditor.editor.IViewZone;
  }>();

  const modelTextValue = useDerivedEditorValue({
    editor,
    getValue: _.identity,
    withPosition: false,
  });

  const { href, origin } = window.location;
  const pageURL = href.split('#')[0];
  const history = useHistory();

  const { locationToSDL, sdlFlags } = useInternalGraphLinking();

  const handleOnLinkClick = React.useCallback(() => {
    const sdlHashParams = getHashParamsFromLineNumber(typeDefs, lineNumber);

    if (sdlHashParams) {
      const location = locationToSDL({
        selectedSchema,
        graphqlTypeName: sdlHashParams.graphqlTypeName,
        fieldName: sdlHashParams.fieldName,
        argumentName: sdlHashParams.argumentName,
        directiveName: sdlHashParams.directiveName,
      });

      if (sdlFlags.redirectOnLineCopy) {
        copy(`${pageURL}#${location.hash}`);
        history.push(location);
      } else {
        const { pathname = '', search = '', hash = '' } = location;
        copy(`${origin}${pathname}?${search}#${hash}`);
      }
    }
  }, [
    selectedSchema,
    history,
    lineNumber,
    pageURL,
    typeDefs,
    locationToSDL,
    origin,
    sdlFlags,
  ]);

  const afterLineNumber = lineNumber - 1;

  React.useEffect(() => {
    // A view zone is a full horizontal rectangle that 'pushes' text down. The editor reserves space for view zones when rendering.
    // we position ours absolutely so that they don't push anything down and are just rendered over a line
    editor.changeViewZones((changeAccessor) => {
      marginDomNode.className = 'z-20';
      const newZone = {
        afterLineNumber,
        heightInLines: 0,
        domNode: document.createElement('div'),
        marginDomNode,
      };
      currentViewZone.current = {
        zone: newZone,
        zoneId: changeAccessor.addZone(newZone),
      };
    });

    return () => {
      const lastZone = currentViewZone.current;
      if (!lastZone) return;
      editor.changeViewZones((changeAccessor) => {
        changeAccessor.removeZone(lastZone.zoneId);
      });
      currentViewZone.current = undefined;
    };
    // when models change, view zones are lost so it needs to be in the dependency array
  }, [afterLineNumber, editor, marginDomNode, modelTextValue]);

  return (
    <>
      {createPortal(
        <Tooltip
          label={`Click to copy link to this ${definitionType}`}
          clickedLabel="Copied"
          className="-ml-12"
        >
          <div className="h-5 cursor-pointer" onClick={handleOnLinkClick}>
            <IconLink
              className={classnames(
                '-mt-0.5 ml-2 text-icon-primary',
                showLink ? 'visible' : 'invisible',
              )}
            />
          </div>
        </Tooltip>,
        marginDomNode,
      )}
    </>
  );
};
